import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

// cc-common imports
import { IconConstants } from 'cc-common';

// component imports
import { custom } from 'devextreme/ui/dialog';
import { DxTreeViewComponent } from 'devextreme-angular';

// service imports
import { BookmarkService } from '../../_services/shared-services/bookmark.service';

// model imports
import { PopMenuInfo } from '../../_models/pop-menu-info';
import { BookmarkInfo } from '../../_models/bookmark-info';

@Component({
    selector: 'app-bookmark-manager',
    templateUrl: './bookmark-manager.component.html',
    styleUrls: ['./bookmark-manager.component.scss']
})
export class BookmarkManagerComponent implements OnInit, OnChanges {
    @ViewChild('treeviewNav', { static: false }) treeviewNav: DxTreeViewComponent;
    @ViewChild('treeviewBookmarked', { static: false }) treeviewBookmarked: DxTreeViewComponent;

    @Input() sideMenuItems: PopMenuInfo[];
    @Input() visible: boolean;

    @Output() onClose = new EventEmitter<boolean>();

    navItems: PopMenuInfo[] = [];
    bookmarkedItems: PopMenuInfo[] = [];
    disabledItems: PopMenuInfo[] = [];
    unSavedChanges = false;
    navTreeExpanCollapseIcon: string;
    bookmarkTreeExpandCollapseIcon: string;

    iconClose = IconConstants.CLOSE;
    iconSave = IconConstants.SAVE;

    constructor(private readonly bookmarkService: BookmarkService) {}

    ngOnInit() {
        this.bookmarkedItems = [];
        this.prepareBookmarkedItems(); // It is important to set bookmarked item list first, as active navItem bookmarked property is based on it.
        this.navItems = this.prepareNavItems(this.sideMenuItems);
        this.navTreeExpanCollapseIcon = IconConstants.PLUS;
        this.bookmarkTreeExpandCollapseIcon = IconConstants.PLUS;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.sideMenuItems && changes.sideMenuItems.currentValue) {
            this.sideMenuItems = changes.sideMenuItems.currentValue;
        }
    }

    onPopupClose(): void {
        this.onClose.emit(false);
    }

    onDragChange(e): void {
        if (e.fromComponent === e.toComponent) {
            const fromNode = this.findNode(this.getTreeView(e.fromData), e.fromIndex);
            if (!fromNode) {
                e.cancel = true;
            } else {
                const toNode = this.findNode(this.getTreeView(e.toData), this.calculateToIndex(e));
                if (toNode !== null && this.isChildNode(fromNode, toNode)) {
                    e.cancel = true;
                }
            }
        }
    }

    onDragEnd(e): void {
        if (e.fromComponent === e.toComponent && e.fromIndex === e.toIndex) {
            return;
        }

        const fromTreeView = this.treeviewNav.instance;
        const toTreeView = this.treeviewBookmarked.instance;

        const fromNode = this.findNode(fromTreeView, e.fromIndex);
        const toNode = this.findNode(toTreeView, this.calculateToIndex(e));

        if (e.dropInsideItem && toNode !== null) {
            return;
        }

        if (fromNode) {
            const bookmarkItemsToAdd = this.getBookmarkItemsToAdd(fromNode.itemData, this.navItems);
            this.addNewBookmarkedItems(bookmarkItemsToAdd);
            this.treeviewBookmarked.instance.option('items', this.bookmarkedItems);
            this.unSavedChanges = true;
        }
    }

    onReorderChange(e): void {
        const fromNode = this.findNode(this.treeviewBookmarked.instance, e.fromIndex);
        if (fromNode) {
            const fromData = this.bookmarkedItems.find((d) => d.name === fromNode.itemData.name);
            const fromIndex = this.bookmarkedItems.indexOf(fromData);

            this.bookmarkedItems.splice(fromIndex, 1);
            if (fromData.items.length > 0) {
                fromData.items.forEach((item) => {
                    const itemIndex = this.bookmarkedItems.findIndex((d) => d.name === item.name);
                    if (itemIndex !== -1) {
                        const node = this.bookmarkedItems[itemIndex];
                        this.bookmarkedItems.splice(itemIndex, 1);
                        this.bookmarkedItems.splice(e.toIndex, 0, node);
                    }
                });
            }
            this.bookmarkedItems.splice(e.toIndex, 0, fromData);
            this.treeviewBookmarked.instance.option('items', this.bookmarkedItems);
            this.unSavedChanges = true;
        }
    }

    removeBookmark(item): void {
        this.removeBookmarkItem(item);
        this.unSavedChanges = true;
    }

    onBookmarkSave(): void {
        this.saveBookmarkItems();
    }

    onBookmarkClose(): void {
        if (this.unSavedChanges) {
            const customDialog = custom({
                title: 'Confirmation',
                messageHtml: 'Do you want to save the changes?',
                buttons: [
                    {
                        text: 'Yes',
                        type: 'success',
                        onClick: (e) => {
                            this.saveBookmarkItems();
                        }
                    },
                    {
                        text: 'No',
                        type: 'default',
                        onClick: (e) => {
                            this.onClose.emit(false);
                        }
                    }
                ]
            });
            customDialog.show();
        } else {
            this.onClose.emit(false);
        }
    }

    toggleNavViewTreeExpansion(): void {
        if (this.navTreeExpanCollapseIcon === IconConstants.PLUS) {
            this.treeviewNav.instance.expandAll();
            this.navTreeExpanCollapseIcon = IconConstants.MINUS;
        } else if (this.navTreeExpanCollapseIcon === IconConstants.MINUS) {
            this.treeviewNav.instance.collapseAll();
            this.navTreeExpanCollapseIcon = IconConstants.PLUS;
        }
    }

    toggleBookmarkTreeExpansion(): void {
        if (this.bookmarkTreeExpandCollapseIcon === IconConstants.PLUS) {
            this.treeviewBookmarked.instance.expandAll();
            this.bookmarkTreeExpandCollapseIcon = IconConstants.MINUS;
        } else if (this.bookmarkTreeExpandCollapseIcon === IconConstants.MINUS) {
            this.treeviewBookmarked.instance.collapseAll();
            this.bookmarkTreeExpandCollapseIcon = IconConstants.PLUS;
        }
    }

    removeAllBookmark(): void {
        const customDialog = custom({
            title: 'Confirmation',
            messageHtml: 'Do you want to remove all bookmared items?',
            buttons: [
                {
                    text: 'Yes',
                    type: 'success',
                    onClick: (e) => {
                        this.bookmarkedItems = [];
                        this.sideMenuItems = this.resetBookMarkItems(this.sideMenuItems);
                        this.navItems = this.prepareNavItems(this.sideMenuItems);
                        this.unSavedChanges = true;
                    }
                },
                {
                    text: 'No',
                    type: 'default',
                    onClick: (e) => {
                        return;
                    }
                }
            ]
        });
        customDialog.show();
    }

    private resetBookMarkItems(items: PopMenuInfo[]): PopMenuInfo[] {
        items.forEach((item) => {
            item.disable = false;
            item.bookmarked = false;

            if (item.items.length > 0) {
                item.items = this.resetBookMarkItems(item.items);
            }
        });

        return items;
    }

    private getBookmarkItemsToAdd(nodeToAdd, navItems): PopMenuInfo[] {
        const bookmarkItems: PopMenuInfo[] = [];

        navItems.forEach((node) => {
            if (node.name === nodeToAdd.name) {
                this.markNodeHierarachy(node, true);
            }

            if (node.bookmarked) {
                let bookmarkNode: PopMenuInfo = new PopMenuInfo();
                bookmarkNode = Object.assign(bookmarkNode, node);
                bookmarkNode.items = [];
                bookmarkItems.push(bookmarkNode);
            }
        });

        return bookmarkItems;
    }

    private markNodeHierarachy(parent, mark): void {
        this.navItems.forEach((node) => {
            if (node.name === parent.name) {
                node.bookmarked = mark;
            }
            if (node.parentId === parent.id) {
                node.bookmarked = mark;
                this.markNodeHierarachy(node, mark);
            }
        });
    }

    private getTreeView(treeName): any {
        return treeName === 'active' ? this.treeviewNav.instance : this.treeviewBookmarked.instance;
    }

    private calculateToIndex(e): void {
        if (e.fromComponent !== e.toComponent || e.dropInsideItem) {
            return e.toIndex;
        }

        return e.fromIndex >= e.toIndex ? e.toIndex : e.toIndex + 1;
    }

    private findNode(treeView, index): any {
        const nodeElement = treeView.element().querySelectorAll('.dx-treeview-node')[index];
        if (nodeElement) {
            return this.findNodeById(treeView.getNodes(), nodeElement.getAttribute('data-item-id'));
        }
        return null;
    }

    private findNodeById(nodes, id): any {
        for (const node of nodes) {
            if (node.itemData.id === id) {
                return node;
            }
            if (node.items.length > 0) {
                const child = this.findNodeById(node.items, id);
                if (child !== null) {
                    return child;
                }
            }
        }
        return null;
    }

    private removeBookmarkItem(nodeToRemove): void {
        const newItems: PopMenuInfo[] = [];
        const itemsToRemove: PopMenuInfo[] = [nodeToRemove];
        if (nodeToRemove.parentId) {
            const parentNode = this.bookmarkedItems.find((d) => d.id === nodeToRemove.parentId);
            const childNodes = this.bookmarkedItems.filter((d) => d.parentId === parentNode.id);
            if (childNodes.length === 1) {
                itemsToRemove.push(parentNode);
            }
        }
        this.bookmarkedItems.forEach((node) => {
            itemsToRemove.forEach((rNode) => {
                if (node.name === rNode.name) {
                    node.bookmarked = false;
                    this.markNodeHierarachy(node, false);
                }
            });
            if (node.bookmarked) {
                newItems.push(node);
            }
        });

        this.bookmarkedItems = newItems;
    }

    private isChildNode(parentNode, childNode): boolean {
        let parent = childNode.parent;
        while (parent !== null) {
            if (parent.itemData.id === parentNode.itemData.id) {
                return true;
            }
            parent = parent.parent;
        }
        return false;
    }

    private saveBookmarkItems(): void {
        const savedItems = this.prepareForSave();
        this.bookmarkService.saveBookmarkItems(savedItems);
        this.unSavedChanges = false;
    }

    private prepareForSave(): BookmarkInfo[] {
        const saveBookmark: BookmarkInfo[] = [];

        this.bookmarkedItems.forEach((item) => {
            const itemToSave: BookmarkInfo = new BookmarkInfo();
            const index = this.bookmarkedItems.indexOf(item);
            itemToSave.name = item.name;
            itemToSave.visibleIndex = index;

            saveBookmark.push(itemToSave);
        });

        return saveBookmark;
    }

    private prepareNavItems(items: PopMenuInfo[]): PopMenuInfo[] {
        let navItems: PopMenuInfo[] = [];

        items.forEach((item) => {
            if (!item.disable) {
                let obj: PopMenuInfo = new PopMenuInfo();
                obj = Object.assign(obj, item);
                obj.bookmarked = this.getIsBookmarked(obj, this.bookmarkedItems);
                obj.selected = false;
                navItems.push(obj);
                if (item.items.length > 0) {
                    navItems = navItems.concat(this.prepareNavItems(item.items));
                }
            }
        });

        return navItems;
    }

    private addNewBookmarkedItems(itemsToAdd: PopMenuInfo[]): void {
        itemsToAdd.forEach((item) => {
            if (!this.bookmarkedItems.find((d) => d.name === item.name)) {
                item.bookmarked = true;
                if (
                    !this.bookmarkedItems.find((d) => d.id === item.parentId) &&
                    !itemsToAdd.find((d) => d.id === item.parentId)
                ) {
                    item.parentId = null;
                }
                item.visibleIndex = this.bookmarkedItems.length + 1;
                this.bookmarkedItems.push(item);
            }
        });
    }

    private prepareBookmarkedItems(): void {
        let disabledItems: PopMenuInfo[] = [];
        let bookmarkedItems: PopMenuInfo[] = [];
        this.sideMenuItems.forEach((item) => {
            let obj: PopMenuInfo = new PopMenuInfo();
            obj = Object.assign(obj, item);
            if (obj.bookmarked) {
                if (obj.disable) {
                    disabledItems.push(obj);
                } else {
                    bookmarkedItems.push(obj);
                }
            }

            if (obj.items && obj.items.length > 0) {
                const bookmarkChildItems = this.getBookmarkedChildItems(obj.items);
                if (bookmarkChildItems.length > 0) {
                    bookmarkChildItems.forEach((child) => {
                        if (
                            !bookmarkedItems.find((d) => d.id === child.parentId) &&
                            !bookmarkChildItems.find((d) => d.id === child.parentId)
                        ) {
                            child.parentId = null;
                        }
                    });
                    bookmarkedItems = bookmarkedItems.concat(bookmarkChildItems);
                }
                disabledItems = disabledItems.concat(this.getDisabledBookmarkedChildItems(obj.items));
            }
        });

        this.bookmarkedItems = bookmarkedItems
            .concat(disabledItems)
            .sort((a, b) => (a.visibleIndex > b.visibleIndex ? 1 : -1));
        this.unSavedChanges = false;
    }

    private getDisabledBookmarkedChildItems(items): PopMenuInfo[] {
        let disabledItems: PopMenuInfo[] = [];

        items.forEach((item) => {
            let obj: PopMenuInfo = new PopMenuInfo();
            obj = Object.assign(obj, item);
            if (obj.disable && obj.bookmarked) {
                disabledItems.push(obj);
            }

            if (obj.items.length > 0) {
                disabledItems = disabledItems.concat(this.getDisabledBookmarkedChildItems(obj.items));
            }
        });

        return disabledItems;
    }

    private getBookmarkedChildItems(items): PopMenuInfo[] {
        let bookmarkedItems: PopMenuInfo[] = [];

        items.forEach((item) => {
            let obj: PopMenuInfo = new PopMenuInfo();
            obj = Object.assign(obj, item);

            if (!obj.disable && obj.bookmarked) {
                bookmarkedItems.push(obj);
            }

            if (obj.items.length > 0) {
                bookmarkedItems = bookmarkedItems.concat(this.getBookmarkedChildItems(obj.items));
            }
        });

        return bookmarkedItems;
    }

    private getIsBookmarked(node: PopMenuInfo, items: PopMenuInfo[]): boolean {
        let isBookmarked = false;
        for (const item of items) {
            if (item.name === node.name) {
                isBookmarked = item.bookmarked;
            } else if (item.items.length > 0) {
                isBookmarked = this.getIsBookmarked(node, item.items);
            }
            if (isBookmarked) {
                break;
            }
        }

        return isBookmarked;
    }
}
